import {
  CLASSIFICATION_KINDS_OF_BRANCH,
  PRODUCT_CLASSIFICATION_AXES,
  PRODUCT_CLASSIFICATION_KINDS,
} from '@brickup/shared/constants'
import type {
  CatalogueTreeItem,
  CatalogueTreeProduct,
  ProductClassificationResponse,
} from '@brickup/shared/dto'
import {
  CATALOGUE_KINDS,
  type CatalogueKind,
  type ProductClassificationKind,
} from '@brickup/shared/enums'
import { CATALOGUE } from '@brickup/shared/locales/de/catalogue'
import { COMMON } from '@brickup/shared/locales/de/common'
import { CONTROLS } from '@brickup/shared/locales/de/controls'
import {
  AVAILABILITY_CONFLICT_LABELS,
  BLOCK_TYPE_LABELS,
  CATALOGUE_KIND_LABELS,
  PRICE_MODE_LABELS,
} from '@brickup/shared/locales/de/enum-labels'
import { FIELD_LABELS } from '@brickup/shared/locales/de/fields'
import {
  ALL_OPTION,
  clearAllPillItem,
  FILTER_ALL,
  oneOf,
  PANE_TAB_PARAM,
  selectedRowFilter,
  stringFilter,
  type ListFilters,
  type ToolbarItem,
} from '@/components/Internal/DataTable'
import { classificationOptions } from '@/components/Internal/ProductClassification/classification-entries'
import { optionsOf } from '@/utils/options-of'

/** A product with no manufacturer recorded — a filterable state of its own. */
const NO_MANUFACTURER = 'none'

/** The three kinds as a choice, for a control that shows one at a time. */
export const CATALOGUE_KIND_OPTIONS = CATALOGUE_KINDS.map(value => ({
  value,
  label: CATALOGUE_KIND_LABELS[value],
}))

export const PRICE_MODE_OPTIONS = optionsOf(PRICE_MODE_LABELS)
/** The same set with the widening option in front, for a select that offers
 *  "any" rather than a pill row that offers "none selected". */
export const PRICE_MODE_FILTER_OPTIONS = [ALL_OPTION, ...PRICE_MODE_OPTIONS]
export const AVAILABILITY_CONFLICT_OPTIONS = optionsOf(
  AVAILABILITY_CONFLICT_LABELS,
)

/**
 * The three availability rules of a measure, in the order the pane lists them.
 *
 * One table rather than the same three names in the count, in the labels and
 * in the JSX: a fourth rule is an entry here, and a rule can no longer end up
 * with another rule's colour.
 */
export const AVAILABILITY_RULES = [
  {
    field: 'disabledWhen',
    tone: 'danger',
    label: CATALOGUE.measure.disabledWhen,
    hint: CATALOGUE.measure.disabledWhenHint,
  },
  {
    field: 'requiredWhen',
    tone: 'warning',
    label: CATALOGUE.measure.requiredWhen,
    hint: CATALOGUE.measure.requiredWhenHint,
  },
  {
    field: 'deselectedWhen',
    tone: 'neutral',
    label: CATALOGUE.measure.deselectedWhen,
    hint: CATALOGUE.measure.deselectedWhenHint,
  },
  {
    field: 'hiddenWhen',
    tone: 'muted',
    label: CATALOGUE.measure.hiddenWhen,
    hint: CATALOGUE.measure.hiddenWhenHint,
  },
] as const satisfies readonly {
  field: 'disabledWhen' | 'requiredWhen' | 'deselectedWhen' | 'hiddenWhen'
  tone: 'danger' | 'warning' | 'neutral' | 'muted'
  label: string
  hint: string
}[]

const YES_NO_OPTIONS = [
  { value: 'yes', label: COMMON.yes },
  { value: 'no', label: COMMON.no },
]

/**
 * Everything the catalogue keeps in its URL.
 *
 * The open kind and the open row are filters only in the mechanical
 * sense: they ride in the same query string and are read back on reload, but
 * they select the list rather than narrow it, so neither counts towards "the
 * list is filtered" and neither is undone by a reset.
 *
 * The kind-specific filters share one schema. Which of them applies is decided
 * per kind by `catalogueItemFilter`, and switching kind widens them
 * all.
 */
export const catalogueListFilters = {
  kind: {
    ...oneOf<CatalogueKind>(CATALOGUE_KINDS, 'MEASURE'),
    countsAsFilter: false,
    resettable: false,
  },
  selected: selectedRowFilter(),
  search: stringFilter(),
  usage: oneOf<string>([FILTER_ALL, 'used', 'unused'], FILTER_ALL),
  priceMode: oneOf<string>(
    [FILTER_ALL, ...Object.keys(PRICE_MODE_LABELS)],
    FILTER_ALL,
  ),
  // Widened past the sentinel: a manufacturer is admin-authored text, so the
  // value set is whatever the kind happens to hold.
  manufacturer: stringFilter<string>(FILTER_ALL),
  // The classification ids, for the same reason: the lists are kept by an
  // admin, so the value set is whatever the kind happens to hold.
  category: stringFilter<string>(FILTER_ALL),
  group: stringFilter<string>(FILTER_ALL),
  designLine: stringFilter<string>(FILTER_ALL),
  standard: stringFilter<string>(FILTER_ALL),
  inSummary: oneOf<string>(
    [FILTER_ALL, ...YES_NO_OPTIONS.map(o => o.value)],
    FILTER_ALL,
  ),
  blockType: oneOf<string>(
    [FILTER_ALL, ...Object.keys(BLOCK_TYPE_LABELS)],
    FILTER_ALL,
  ),
}

export type CatalogueListFilters = typeof catalogueListFilters
type Values = ListFilters<CatalogueListFilters>['values']

/**
 * The shared axis table, narrowed to this screen's own two types: the filter
 * key has to be one the schema declares and the column one a product row
 * carries, and nothing else checks that pair.
 */
const CLASSIFICATION_AXES = PRODUCT_CLASSIFICATION_AXES satisfies Record<
  ProductClassificationKind,
  { filter: keyof Values; column: keyof CatalogueTreeProduct }
>

/** The filters that belong to one kind and mean nothing in the next. */
const KIND_SPECIFIC = [
  'priceMode',
  'manufacturer',
  'category',
  'group',
  'designLine',
  'standard',
  'inSummary',
] as const

/** What a kind switch has to widen, so it cannot carry a filter across. */
export const CATALOGUE_KIND_FILTER_DEFAULTS = Object.fromEntries(
  KIND_SPECIFIC.map(key => [key, catalogueListFilters[key].defaultValue]),
) as { [K in (typeof KIND_SPECIFIC)[number]]: string }

/**
 * A deep link into the catalogue. Keeping it beside the schema is what
 * ties the param names to the filters that read them.
 */
export function catalogueLink(
  kind: CatalogueKind,
  selected?: number,
  /** Which tab of the entry's pane to open — the id `PaneTabs` gave it. A
   *  link that names the screen but not the tab lands beside what it meant. */
  pane?: string,
) {
  return {
    to: '/internal/catalogue',
    search: {
      kind,
      ...(selected != null && { selected }),
      ...(pane && { [PANE_TAB_PARAM]: pane }),
    },
  } as const
}

/** Every manufacturer the open products tree names, in German order. */
export function manufacturerOptions(
  items: readonly CatalogueTreeItem[] | undefined,
): { value: string; label: string }[] {
  const names = new Set<string>()
  for (const item of items ?? []) {
    if ('manufacturer' in item && item.manufacturer)
      names.add(item.manufacturer)
  }
  return [...names]
    .sort((a, b) => a.localeCompare(b, 'de'))
    .map(name => ({ value: name, label: name }))
}

/**
 * The predicate the toolbar's filters add on top of the search, or `undefined`
 * where nothing is set — which is what tells the row builder it may leave the
 * folders alone.
 */
export function catalogueItemFilter(
  kind: CatalogueKind,
  values: Values,
): ((item: CatalogueTreeItem) => boolean) | undefined {
  const checks: ((item: CatalogueTreeItem) => boolean)[] = []

  if (values.usage !== FILTER_ALL) {
    const wantUsed = values.usage === 'used'
    checks.push(item => item.usageCount > 0 === wantUsed)
  }

  if (kind === 'MEASURE' && values.priceMode !== FILTER_ALL) {
    checks.push(
      item => 'priceMode' in item && item.priceMode === values.priceMode,
    )
  }

  if (kind === 'PRODUCT' && values.manufacturer !== FILTER_ALL) {
    checks.push(
      item =>
        'manufacturer' in item &&
        (item.manufacturer || NO_MANUFACTURER) === values.manufacturer,
    )
  }

  // The axes this branch is filed by, and no others: a parameter for an axis
  // the branch does not carry would narrow the list to nothing with no
  // control on screen to clear it.
  for (const axis of CLASSIFICATION_KINDS_OF_BRANCH[kind]) {
    const { filter, column } = CLASSIFICATION_AXES[axis]
    if (values[filter] === FILTER_ALL) continue
    const wanted = Number(values[filter])
    checks.push(
      item =>
        column in item && (item as CatalogueTreeProduct)[column] === wanted,
    )
  }

  if (kind === 'FORMULA' && values.inSummary !== FILTER_ALL) {
    const wanted = values.inSummary === 'yes'
    checks.push(item => 'inSummary' in item && item.inSummary === wanted)
  }

  if (checks.length === 0) return undefined
  return item => checks.every(check => check(item))
}

/**
 * Everything `catalogueItemFilter` reads, as one value.
 *
 * The row builder rebuilds the whole tree whenever the predicate's identity
 * changes, so it is memoised on this rather than on `values` — which also
 * carries the open row, and would rebuild every row on a click. Derived from
 * the schema, so a filter added there is covered without touching the caller.
 */
export function catalogueFilterSignature(
  kind: CatalogueKind,
  values: Values,
): string {
  return [kind, values.usage, ...KIND_SPECIFIC.map(key => values[key])].join(
    '\u0000',
  )
}

/**
 * Whether anything narrows the open catalogue.
 *
 * Not `filters.hasActiveFilters`, which counts every kind-specific filter
 * whatever the catalogue: a bookmarked `?kind=PRODUCT&priceMode=FIXED` would
 * otherwise report a filtered list while showing neither the filter nor its
 * effect.
 */
export function catalogueHasActiveFilters(
  kind: CatalogueKind,
  values: Values,
): boolean {
  return values.search.length > 0 || !!catalogueItemFilter(kind, values)
}

/** The selects a branch offers beyond the lists it is filed by, which the
 *  toolbar adds for every branch that carries any. */
const KIND_FILTER_ITEMS = {
  MEASURE: filters => [
    filters.toolbarItem.select('priceMode', PRICE_MODE_FILTER_OPTIONS, {
      placeholder: CATALOGUE.measure.pricing,
    }),
  ],
  PRODUCT: (filters, manufacturers) => [
    filters.toolbarItem.select(
      'manufacturer',
      [
        ALL_OPTION,
        { value: NO_MANUFACTURER, label: CATALOGUE.filters.noManufacturer },
        ...manufacturers,
      ],
      {
        placeholder: CATALOGUE.columns.manufacturer,
      },
    ),
  ],
  // Filed by three of the four lists and narrowed by nothing else.
  LABOUR: () => [],
  PRICE_GROUP: () => [],
  FORMULA: filters => [
    filters.toolbarItem.select('inSummary', [ALL_OPTION, ...YES_NO_OPTIONS], {
      placeholder: CATALOGUE.columns.inSummary,
    }),
  ],
  // What kind of term a Preisbaustein is. Which measures read it is the usage
  // filter every branch carries.
  PRICE_BLOCK: filters => [
    filters.toolbarItem.select(
      'blockType',
      [ALL_OPTION, ...optionsOf(BLOCK_TYPE_LABELS)],
      { placeholder: COMMON.type },
    ),
  ],
} satisfies Record<
  CatalogueKind,
  (
    filters: ListFilters<CatalogueListFilters>,
    manufacturers: { value: string; label: string }[],
  ) => ToolbarItem[]
>

/**
 * The tree toolbar: the clear-all pill, the filter this catalogue offers, the
 * usage filter every catalogue offers, and the search box.
 */
export function catalogueToolbarItems(
  kind: CatalogueKind,
  filters: ListFilters<CatalogueListFilters>,
  manufacturers: { value: string; label: string }[],
  classifications: ProductClassificationResponse[],
): ToolbarItem[] {
  return [
    clearAllPillItem(
      !catalogueHasActiveFilters(kind, filters.values),
      filters.reset,
    ),
    // One select per list this branch is filed by, from the same record the
    // row predicate reads — so a parameter the toolbar does not offer cannot
    // narrow the list to nothing. A product is filed by four axes and none of
    // them nests in another; narrowing by two is how a reader gets to one
    // shelf.
    ...CLASSIFICATION_KINDS_OF_BRANCH[kind].map(axis =>
      filters.toolbarItem.select(
        CLASSIFICATION_AXES[axis].filter,
        [ALL_OPTION, ...classificationOptions(classifications, axis)],
        { placeholder: FIELD_LABELS[CLASSIFICATION_AXES[axis].fieldKey].label },
      ),
    ),
    ...KIND_FILTER_ITEMS[kind](filters, manufacturers),
    filters.toolbarItem.select(
      'usage',
      [
        ALL_OPTION,
        { value: 'used', label: CATALOGUE.filters.used },
        { value: 'unused', label: CATALOGUE.filters.unused },
      ],
      { placeholder: CATALOGUE.filters.usage },
    ),
    filters.toolbarItem.search('search', {
      placeholder: CONTROLS.searchPlaceholder,
    }),
  ]
}

/**
 * One classification axis as a dropdown, for a surface that holds its filter
 * state itself rather than in the URL.
 *
 * Same options and same placeholder as the tree's toolbar offers, so the
 * picker and the catalogue narrow by the same words.
 */
export const classificationFilter = (
  kind: ProductClassificationKind,
  entries: ProductClassificationResponse[],
  value: string,
  onChange: (next: string) => void,
): ToolbarItem => ({
  type: 'select',
  value,
  onChange,
  options: [ALL_OPTION, ...classificationOptions(entries, kind)],
  placeholder: FIELD_LABELS[CLASSIFICATION_AXES[kind].fieldKey].label,
})

/** Whether a product passes every axis a surface has narrowed by. */
export const matchesClassification = (
  product: CatalogueTreeProduct,
  values: Record<ProductClassificationKind, string>,
): boolean =>
  PRODUCT_CLASSIFICATION_KINDS.every(kind => {
    const chosen = values[kind]
    return (
      chosen === FILTER_ALL ||
      product[CLASSIFICATION_AXES[kind].column] === Number(chosen)
    )
  })

/**
 * The price-mode dropdown, as both catalogue pickers declare it.
 *
 * A `ToolbarItem` rather than an element: `FilterToolbar` takes its controls
 * as data, and the two pickers were spelling the same four fields out twice.
 */
export const priceModeFilter = (
  value: string,
  onChange: (next: string) => void,
): ToolbarItem => ({
  type: 'select',
  value,
  onChange,
  options: PRICE_MODE_FILTER_OPTIONS,
  placeholder: CATALOGUE.measure.pricing,
})
